import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Check, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    key: "standard", name: "Standard Premium", price: "₹299", sub: "/ month  ·  $4.99",
    icon: Sparkles, tint: "from-brand/20",
    features: ["Remove all ads", "Full persistent memory", "Unlimited Flux selfies", "ElevenLabs voice replies"],
  },
  {
    key: "vip", name: "VIP God-Mode", price: "₹799", sub: "/ month  ·  $14.99",
    icon: Crown, tint: "from-amber-500/20", highlight: true,
    features: ["Everything in Standard", "Unlimited live voice calls", "120 min HD video calls", "All premium wardrobe unlocked"],
  },
];

export default function PricingPlans({ open, onClose }) {
  const { user, setUser } = useAuth();

  const subscribe = async (plan) => {
    try {
      const { data } = await api.post("/user/subscribe", { plan });
      setUser(data);
      toast.success(`${plan === "vip" ? "VIP God-Mode" : "Standard Premium"} activated ✨`);
      onClose();
    } catch { toast.error("Subscription failed"); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-surface border-white/10 w-full sm:max-w-md p-0 overflow-y-auto" data-testid="pricing-plans">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="font-heading text-xl">Upgrade your connection</SheetTitle>
        </SheetHeader>
        <div className="p-6 space-y-5">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const active = user?.plan === p.key;
            return (
              <div key={p.key} data-testid={`plan-${p.key}`}
                className={`group relative rounded-3xl border p-6 overflow-hidden ${p.highlight ? "border-brand/50" : "border-white/10"} bg-slate-900/70 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.55)]`}>
                <div className="absolute inset-0 opacity-10 group-hover:opacity-30 plasma group-hover:plasma-fast transition-opacity duration-300 pointer-events-none" />
                <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full blur-[70px] bg-gradient-to-br ${p.tint} to-transparent`} />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5 text-brand" />
                    <h3 className="font-heading font-bold text-lg">{p.name}</h3>
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-heading text-3xl font-bold">{p.price}</span>
                    <span className="text-xs text-muted-foreground">{p.sub}</span>
                  </div>
                  <div className="space-y-2 mb-5">
                    {p.features.map((f) => (
                      <div key={f} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-brand shrink-0" /> {f}
                      </div>
                    ))}
                  </div>
                  <Button data-testid={`subscribe-${p.key}`} disabled={active} onClick={() => subscribe(p.key)}
                    className={`w-full h-12 rounded-xl font-semibold transition-colors ${p.highlight ? "bg-brand hover:bg-brand-hover text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}>
                    {active ? "Current plan" : "Subscribe"}
                  </Button>
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-muted-foreground/60 text-center">Razorpay checkout bridge is stubbed in this build — subscribing activates the plan instantly for preview.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
